import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateInsuranceProduct,
  AdminDeleteInsuranceProduct,
  AdminFetchInsuranceProducts,
  AdminUpdateInsuranceProduct,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateInsuranceProductPayload,
  DeleteInsuranceProductPayload,
  InsuranceProduct,
  UpdateInsuranceProductPayload,
} from 'src/interfaces/insuranceProduct'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface InsuranceProductBodyFields {
  name: string
  description?: string
  explanation?: string
}

/**
 * Builds the request body field by field rather than forwarding the action payload.
 *
 * Both the POST and the PUT run `whitelist + forbidNonWhitelisted`, so forwarding the payload
 * would send `insuranceProductId` alongside the fields and 400 the whole request.
 *
 * Empty optionals are omitted rather than sent as `''`: the backend update service filters the
 * body to truthy values, so an empty string is ignored anyway — and sending one would tell the
 * admin a value was cleared when it was not. `name` is always present, which is what
 * guarantees the PUT never hits the 'At least one field must be provided' rejection.
 */
const buildInsuranceProductBody = (
  fields: InsuranceProductBodyFields,
): InsuranceProductBodyFields => {
  const body: InsuranceProductBodyFields = { name: fields.name }

  if (fields.description) {
    body.description = fields.description
  }

  if (fields.explanation) {
    body.explanation = fields.explanation
  }

  return body
}

export function* onFetchInsuranceProducts(action: AsyncAction<void>) {
  try {
    // The endpoint answers with a raw array rather than `{ data, total }`; the normalizer
    // handles both, so this call site stays correct if it is ever wrapped.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInsuranceProducts,
      { method: 'GET' },
    )

    const insuranceProducts = normalizeApiListPayload<InsuranceProduct>(payload)

    yield put(AdminFetchInsuranceProducts.success(insuranceProducts))

    if (action.next) {
      action.next(null, insuranceProducts)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insuranceProduct.onFetchInsuranceProducts] error',
    })

    yield put(AdminFetchInsuranceProducts.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load insurance products'))
    }
  }
}

export function* onCreateInsuranceProduct(action: AsyncAction<CreateInsuranceProductPayload>) {
  try {
    const { payload }: { payload: InsuranceProduct } = yield call(
      callSecureApi,
      Endpoints.AdminCreateInsuranceProduct,
      {
        method: 'POST',
        body: buildInsuranceProductBody(action.payload),
      },
    )

    yield put(AdminCreateInsuranceProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insuranceProduct.onCreateInsuranceProduct] error',
    })

    yield put(AdminCreateInsuranceProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create insurance product'))
    }
  }
}

export function* onUpdateInsuranceProduct(action: AsyncAction<UpdateInsuranceProductPayload>) {
  const insuranceProductId = action.payload?.insuranceProductId

  if (!insuranceProductId) {
    yield put(AdminUpdateInsuranceProduct.failed(new Error('Missing insuranceProductId')))

    if (action.next) {
      action.next('Missing insuranceProductId')
    }

    return
  }

  try {
    const { payload }: { payload: InsuranceProduct } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateInsuranceProduct,
      {
        method: 'PUT',
        params: [{ field: ':insuranceProductId', value: insuranceProductId }],
        body: buildInsuranceProductBody(action.payload),
      },
    )

    yield put(AdminUpdateInsuranceProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insuranceProduct.onUpdateInsuranceProduct] error',
    })

    yield put(AdminUpdateInsuranceProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update insurance product'))
    }
  }
}

export function* onDeleteInsuranceProduct(action: AsyncAction<DeleteInsuranceProductPayload>) {
  const insuranceProductId = action.payload?.insuranceProductId

  if (!insuranceProductId) {
    yield put(AdminDeleteInsuranceProduct.failed(new Error('Missing insuranceProductId')))

    if (action.next) {
      action.next('Missing insuranceProductId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.AdminDeleteInsuranceProduct, {
      method: 'DELETE',
      // This endpoint uses 403 for a domain rule ('Insurance product is in use'), not for an
      // expired token — which needs no opt-out now that `callSecureApi` passes every error
      // through untouched.
      params: [{ field: ':insuranceProductId', value: insuranceProductId }],
    })

    yield put(AdminDeleteInsuranceProduct.success(insuranceProductId))

    if (action.next) {
      action.next(null, insuranceProductId)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insuranceProduct.onDeleteInsuranceProduct] error',
    })

    yield put(AdminDeleteInsuranceProduct.failed(error))

    if (action.next) {
      // A 403 here carries 'Insurance product is in use', which now reaches the admin verbatim.
      action.next(resolveErrorMessage(error, 'Failed to delete insurance product'))
    }
  }
}

export function* insuranceProductSaga() {
  // A refresh or a navigation re-triggers the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchInsuranceProducts.type.REQUEST, onFetchInsuranceProducts)
  // Mutations must each run.
  yield takeEvery(AdminCreateInsuranceProduct.type.REQUEST, onCreateInsuranceProduct)
  yield takeEvery(AdminUpdateInsuranceProduct.type.REQUEST, onUpdateInsuranceProduct)
  yield takeEvery(AdminDeleteInsuranceProduct.type.REQUEST, onDeleteInsuranceProduct)
}
